import { useState, useCallback, useEffect } from 'react';
import { Check, FlaskConical, RefreshCw, AlertCircle } from 'lucide-react';
import LabBench from '../../LabBench';
import InfoBox from '../../InfoBox';
import { Burette, ErlenmeyerFlask } from '@/components/lab';

const stepInfoContent: Record<number, { title: string; content: string; formulas?: string[] }> = {
  1: {
    title: 'Preparing New Samples',
    content: 'Each trial requires a fresh antacid sample prepared identically to the first. Weigh, crush, transfer, add water, and add indicator the same way each time. Consistency in technique minimizes systematic errors.',
    formulas: ['Sample prep: weigh -> crush -> transfer -> add water -> add indicator']
  },
  2: {
    title: 'Replicating the Titration',
    content: 'Perform the titration with the same technique as Trial 1. Control the stopcock carefully near the endpoint. Record initial and final burette readings precisely. Minor variations are expected and acceptable.',
    formulas: ['Expected variation: +/- 0.2 mL between trials']
  },
  3: {
    title: 'Checking Concordance',
    content: 'Concordant results are trials that agree within 0.20 mL. At least two concordant trials are needed for reliable calculations. If results differ by more than 0.20 mL, additional trials may be necessary.',
    formulas: ['Concordance criterion: |V1 - V2| <= 0.20 mL', 'Use concordant values for average']
  }
};

// Methyl Orange indicator colors
const INDICATOR_COLORS = {
  basic: '#FFD700',
  transition1: '#FFA500',
  transition2: '#FF8C00',
  transition3: '#FF6347',
  endpoint: '#FF4500'
};

interface TrialData {
  initial: number;
  final: number;
  volume: number;
}

export default function Part5RepeatTrials() {
  const [currentTrial, setCurrentTrial] = useState(1);
  const [trials, setTrials] = useState<TrialData[]>([]);
  const [samplePrepared, setSamplePrepared] = useState(false);
  const [titrationComplete, setTitrationComplete] = useState(false);
  const [concordanceChecked, setConcordanceChecked] = useState(false);

  const [buretteReading, setBuretteReading] = useState(0.75);
  const [solutionColor, setSolutionColor] = useState(INDICATOR_COLORS.basic);
  const [stopcockOpen, setStopcockOpen] = useState(false);
  const [stirrerOn, setStirrerOn] = useState(false);
  const [titrating, setTitrating] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [showInfo, setShowInfo] = useState<number | null>(null);

  // Base target for concordant results (with small variations)
  const baseTarget = 19.5;
  const [currentTarget] = useState(() => baseTarget + (Math.random() * 0.6 - 0.3));

  const completedSteps = [samplePrepared, titrationComplete, concordanceChecked].filter(Boolean).length;

  // Titration simulation
  useEffect(() => {
    if (titrating && stopcockOpen && !titrationComplete) {
      const interval = setInterval(() => {
        setBuretteReading(prev => {
          const newReading = prev + 0.15;
          const progress = (newReading - 0.75) / currentTarget;

          // Color transitions
          if (progress >= 1) {
            setTitrationComplete(true);
            setSolutionColor(INDICATOR_COLORS.endpoint);
            setStopcockOpen(false);
            setTitrating(false);

            // Record trial
            const trialData: TrialData = {
              initial: 0.75,
              final: parseFloat(newReading.toFixed(2)),
              volume: parseFloat((newReading - 0.75).toFixed(2))
            };
            setTrials(prev => [...prev, trialData]);
            setCurrentStep(3);
            return newReading;
          } else if (progress > 0.9) {
            setSolutionColor(INDICATOR_COLORS.transition3);
          } else if (progress > 0.7) {
            setSolutionColor(INDICATOR_COLORS.transition2);
          } else if (progress > 0.5) {
            setSolutionColor(INDICATOR_COLORS.transition1);
          }

          return newReading;
        });
      }, 150);

      return () => clearInterval(interval);
    }
  }, [titrating, stopcockOpen, titrationComplete, currentTarget]);

  const handlePrepareSample = useCallback(() => {
    if (!samplePrepared) {
      setSamplePrepared(true);
      setStirrerOn(true);
      setCurrentStep(2);
    }
  }, [samplePrepared]);

  const handleStartTitration = useCallback(() => {
    if (samplePrepared && !titrationComplete) {
      setTitrating(true);
      setStopcockOpen(true);
    }
  }, [samplePrepared, titrationComplete]);

  const handleToggleStopcock = useCallback(() => {
    if (titrating && !titrationComplete) {
      setStopcockOpen(prev => !prev);
    }
  }, [titrating, titrationComplete]);

  const handleCheckConcordance = useCallback(() => {
    if (trials.length >= 2 && !concordanceChecked) {
      setConcordanceChecked(true);
    }
  }, [trials.length, concordanceChecked]);

  const handleNextTrial = useCallback(() => {
    if (titrationComplete && currentTrial < 3) {
      setCurrentTrial(prev => prev + 1);
      setSamplePrepared(false);
      setTitrationComplete(false);
      setBuretteReading(0.75);
      setSolutionColor(INDICATOR_COLORS.basic);
      setStopcockOpen(false);
      setStirrerOn(false);
      setTitrating(false);
      setCurrentStep(1);
    }
  }, [titrationComplete, currentTrial]);

  // Check concordance (within 0.20 mL)
  const checkConcordance = () => {
    if (trials.length < 2) return { concordant: false, diff: 0 };
    const volumes = trials.map(t => t.volume);
    const maxDiff = Math.max(...volumes) - Math.min(...volumes);
    return { concordant: maxDiff <= 0.20, diff: maxDiff };
  };

  const concordanceResult = checkConcordance();

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <FlaskConical size={16} />
          <h3>Part V: Repeat Trials (Trial {currentTrial}/3)</h3>
        </div>
        <div className="steps-list">
          {[
            { id: 1, title: 'Prepare New Sample', hint: 'Set up new antacid sample', done: samplePrepared },
            { id: 2, title: 'Repeat Titration', hint: 'Titrate to endpoint', done: titrationComplete },
            { id: 3, title: 'Check Concordance', hint: 'Verify reproducibility', done: concordanceChecked }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${step.done ? 'completed' : ''} ${currentStep === step.id ? 'active' : ''}`}
            >
              <div className={`step-number ${step.done ? 'done' : ''}`}>
                {step.done ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <span className="step-title">{step.title}</span>
                {currentStep === step.id && !step.done && (
                  <span className="step-hint">{step.hint}</span>
                )}
              </div>
              {stepInfoContent[step.id] && (
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                >
                  ?
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Trial Results Table */}
        <div className="trials-table">
          <h4>Trial Results</h4>
          <table>
            <thead>
              <tr>
                <th>Trial</th>
                <th>Initial (mL)</th>
                <th>Final (mL)</th>
                <th>Volume (mL)</th>
              </tr>
            </thead>
            <tbody>
              {trials.map((trial, idx) => (
                <tr key={idx}>
                  <td>{idx + 1}</td>
                  <td>{trial.initial.toFixed(2)}</td>
                  <td>{trial.final.toFixed(2)}</td>
                  <td className="highlight">{trial.volume.toFixed(2)}</td>
                </tr>
              ))}
              {trials.length < 3 && currentTrial <= 3 && (
                <tr className="pending">
                  <td>{trials.length + 1}</td>
                  <td>-</td>
                  <td>-</td>
                  <td>-</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Concordance Result */}
        {trials.length >= 2 && (
          <div className={`concordance-result ${concordanceResult.concordant ? 'success' : 'warning'}`}>
            <h4>Concordance Check</h4>
            <p>Max difference: {concordanceResult.diff.toFixed(2)} mL</p>
            <p className="verdict">
              {concordanceResult.concordant
                ? 'CONCORDANT (within 0.20 mL)'
                : 'NOT CONCORDANT (>0.20 mL difference)'}
            </p>
          </div>
        )}
      </div>

      <LabBench
        experimentName="Repeat Trials"
        completedCount={completedSteps}
        totalSteps={3}
        equipment={['Burette', 'Erlenmeyer flask', 'White tile or paper']}
        labMode="guided"
        currentStep={currentStep}
      >
        <div className="titration-workspace">
          <div className="titration-setup">
            {/* Burette */}
            <div className="burette-section">
              <Burette
                volume={50 - buretteReading}
                maxVolume={50}
                liquidColor="rgba(200, 255, 200, 0.6)"
                stopcock={stopcockOpen ? 'open' : 'closed'}
                onStopcock={titrating && !titrationComplete ? handleToggleStopcock : undefined}
              />
              <div className="burette-reading">
                Reading: {buretteReading.toFixed(2)} mL
              </div>
            </div>

            {/* Flask on Stirrer */}
            <div className="flask-stirrer-combo">
              <ErlenmeyerFlask
                size={250}
                fillLevel={samplePrepared ? 60 : 0}
                fillColor={solutionColor}
                hasStirBar={false}
                stirring={false}
                active={currentStep === 1 && !samplePrepared}
                onClick={!samplePrepared ? handlePrepareSample : undefined}
              />
            </div>
          </div>

          {/* Current Color Display */}
          <div className="color-display">
            <div className="current-color" style={{ background: solutionColor }}>
              <span style={{ color: solutionColor === INDICATOR_COLORS.basic ? '#333' : '#FFF' }}>
                Trial {currentTrial} - {solutionColor === INDICATOR_COLORS.endpoint ? 'ENDPOINT!' : 'In Progress'}
              </span>
            </div>
          </div>

          {/* Status Messages */}
          <div className="status-area">
            {!samplePrepared && (
              <div className="status-message info" onClick={handlePrepareSample} style={{ cursor: 'pointer' }}>
                <RefreshCw size={20} />
                Click to prepare sample for Trial {currentTrial}
              </div>
            )}
            {samplePrepared && !titrating && !titrationComplete && (
              <div className="status-message info" onClick={handleStartTitration} style={{ cursor: 'pointer' }}>
                <FlaskConical size={20} />
                Click to begin titration for Trial {currentTrial}
              </div>
            )}
            {titrating && !titrationComplete && (
              <div className="status-message warning">
                <AlertCircle size={20} />
                Titrating... Click stopcock to control flow
              </div>
            )}
            {titrationComplete && currentTrial < 3 && (
              <div className="status-message success" onClick={handleNextTrial} style={{ cursor: 'pointer' }}>
                <RefreshCw size={20} />
                Trial {currentTrial} complete! Click to start Trial {currentTrial + 1}
              </div>
            )}
            {titrationComplete && currentTrial >= 3 && !concordanceChecked && (
              <div className="status-message success" onClick={handleCheckConcordance} style={{ cursor: 'pointer' }}>
                <Check size={20} />
                All trials complete! Click to check concordance.
              </div>
            )}
            {concordanceChecked && (
              <div className={`status-message ${concordanceResult.concordant ? 'success' : 'warning'}`}>
                <Check size={20} />
                {concordanceResult.concordant
                  ? 'Results are concordant! Ready for calculations.'
                  : 'Results may need additional trials for better precision.'}
              </div>
            )}
          </div>

          {/* Theory Box */}
          <div className="theory-box">
            <h4>Concordant Results</h4>
            <p>
              For reliable results, at least two titrations should agree within 0.20 mL.
              These &ldquo;concordant&rdquo; values indicate reproducible technique and
              provide confidence in the final average.
            </p>
            <p>
              If results differ by more than 0.20 mL, additional trials may be needed
              to identify and correct technique issues.
            </p>
          </div>
        </div>

        <style>{`
          .titration-workspace {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            padding: 20px;
            width: 100%;
          }

          .titration-setup {
            display: flex;
            gap: 60px;
            align-items: flex-end;
            justify-content: center;
          }

          .burette-section {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
          }

          .burette-reading {
            background: #1a1a1a;
            padding: 6px 12px;
            border-radius: 4px;
            font-family: monospace;
            font-size: 14px;
            color: #00ff88;
          }

          .flask-stirrer-combo {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 0;
          }

          .color-display {
            margin-top: 10px;
          }

          .current-color {
            padding: 12px 24px;
            border-radius: 8px;
            min-width: 200px;
            text-align: center;
            border: 2px solid rgba(0,0,0,0.2);
            transition: background-color 0.3s ease;
          }

          .current-color span {
            font-weight: 600;
            font-size: 14px;
          }

          .trials-table {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 12px;
            margin-top: 16px;
          }

          .trials-table h4 {
            margin: 0 0 10px 0;
            font-size: 13px;
            color: var(--secondary);
          }

          .trials-table table {
            width: 100%;
            border-collapse: collapse;
            font-size: 12px;
          }

          .trials-table th, .trials-table td {
            padding: 6px 8px;
            text-align: center;
            border-bottom: 1px solid var(--border);
          }

          .trials-table th {
            color: var(--text-secondary);
            font-weight: 600;
          }

          .trials-table td.highlight {
            color: var(--secondary);
            font-weight: 600;
            font-family: monospace;
          }

          .trials-table tr.pending td {
            color: var(--text-secondary);
            opacity: 0.5;
          }

          .concordance-result {
            border-radius: 8px;
            padding: 12px;
            margin-top: 12px;
          }

          .concordance-result.success {
            background: rgba(0, 168, 107, 0.1);
            border: 1px solid var(--secondary);
          }

          .concordance-result.warning {
            background: rgba(251, 191, 36, 0.1);
            border: 1px solid #FBBF24;
          }

          .concordance-result h4 {
            margin: 0 0 8px 0;
            font-size: 13px;
            color: var(--text-primary);
          }

          .concordance-result p {
            margin: 0 0 4px 0;
            font-size: 12px;
            color: var(--text-secondary);
          }

          .concordance-result .verdict {
            font-weight: 600;
            color: var(--secondary);
          }

          .concordance-result.warning .verdict {
            color: #FBBF24;
          }

          .status-area {
            min-height: 50px;
            display: flex;
            justify-content: center;
          }

          .status-message {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 12px 20px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
          }

          .status-message.info {
            background: rgba(59, 130, 246, 0.2);
            color: #60A5FA;
          }

          .status-message.warning {
            background: rgba(251, 191, 36, 0.2);
            color: #FBBF24;
          }

          .status-message.success {
            background: rgba(0, 168, 107, 0.2);
            color: var(--secondary);
          }

          .theory-box {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 16px;
            max-width: 500px;
            border-left: 3px solid var(--secondary);
          }

          .theory-box h4 {
            margin: 0 0 8px 0;
            color: var(--secondary);
            font-size: 14px;
          }

          .theory-box p {
            margin: 0 0 8px 0;
            font-size: 13px;
            color: var(--text-secondary);
            line-height: 1.5;
          }

          .theory-box p:last-child {
            margin-bottom: 0;
          }

          .step-info-btn {
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: var(--accent);
            color: var(--bg-dark);
            font-size: 11px;
            font-weight: 700;
            border: none;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            transition: transform 0.15s;
          }
          .step-info-btn:hover {
            transform: scale(1.1);
          }
        `}</style>
      </LabBench>

      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp10"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
